import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Box, Grid, Paper, Typography, Button, TextField, IconButton, 
    Card, CardContent, Select, MenuItem, Autocomplete, Divider, Chip, 
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, 
    Tabs, Tab, Tooltip, Checkbox, FormControl, InputLabel, List, ListItem,
    Snackbar, Alert 
} from '@mui/material';

// Iconos
import SendIcon from '@mui/icons-material/Send';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FlagIcon from '@mui/icons-material/Flag';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const DashboardPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const isAdmin = user && user.role === 'administrador';
    const isAsistente = user && user.role === 'asistente';
    const isClinical = user && (user.role === 'administrador' || user.role === 'medico');

    const [loading, setLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(dayjs());
    const [patientsList, setPatientsList] = useState([]);
    const [unifiedContacts, setUnifiedContacts] = useState([]);
    
    const [helpOpen, setHelpOpen] = useState(false);
    const [msgTab, setMsgTab] = useState(0); 
    const [selectedRecipients, setSelectedRecipients] = useState([]); 
    const [messageSubject, setMessageSubject] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [macros, setMacros] = useState([]); 
    
    const [dirTab, setDirTab] = useState(0);
    const [dirForm, setDirForm] = useState({ name: '', value: '', category: 'General' });
    const [selectedDirContact, setSelectedDirContact] = useState(null);

    const [activeLogTab, setActiveLogTab] = useState(0);
    const [selectedLogContacts, setSelectedLogContacts] = useState([]);

    const [adminTasks, setAdminTasks] = useState([
        { id: 1, text: 'Pagar alquiler consultorio', priority: 'alta', completed: false, date: dayjs().format('YYYY-MM-DD') },
        { id: 2, text: 'Llamar a proveedor de insumos', priority: 'media', completed: false, date: dayjs().format('YYYY-MM-DD') },
        { id: 3, text: 'Renovar licencia de software', priority: 'baja', completed: true, date: dayjs().format('YYYY-MM-DD') },
    ]);
    const [newTaskText, setNewTaskText] = useState('');
    const [newTaskPriority, setNewTaskPriority] = useState('media');

    // ✅ Estados para reemplazar alertas nativas
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, content: '', onConfirm: null });

    const fetchUnifiedData = useCallback(async () => {
        if (!user) return; 
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return; // Si no hay token, no intentamos fetch para evitar 401
        }

        try {
            const headers = { 'Authorization': `Bearer ${token}` };
            const results = await Promise.allSettled([
                fetch(`${API_URL}/api/patients`, { headers }),
                fetch(`${API_URL}/api/contacts/unified`, { headers }), 
                fetch(`${API_URL}/api/macros`, { headers }) 
            ]);

            if (results[0].status === 'fulfilled' && results[0].value.ok) setPatientsList(await results[0].value.json());
            if (results[1].status === 'fulfilled' && results[1].value.ok) setUnifiedContacts(await results[1].value.json());
            if (results[2].status === 'fulfilled' && results[2].value.ok) setMacros(await results[2].value.json());

        } catch (e) { console.error("Error dashboard:", e); } 
        finally { setLoading(false); }
    }, [user]);

    useEffect(() => {
        fetchUnifiedData(); 
    }, [fetchUnifiedData]);

 useEffect(() => {
        const loadFromState = async () => {
            // 1. Cargar Asunto y Cuerpo si existen (Desde Auditoría)
            if (location.state?.pendingSubject) {
                setMessageSubject(location.state.pendingSubject);
            }
            if (location.state?.pendingBody) {
                setMessageBody(location.state.pendingBody);
            }
            
            // 2. Cargar Destinatario (Opcional, busca en unified si ya existe)
            if (location.state?.recipientEmail) {
                const found = unifiedContacts.find(c => getContactValue(c) === location.state.recipientEmail);
                if (found) setSelectedRecipients([found]);
                else setSelectedRecipients([location.state.recipientEmail]);
            }

            // 3. Cargar Adjunto (Sea de Gestión Documental o Auditoría)
            if (location.state?.pendingAttachment) {
                // Si ya tenemos un asunto/body no lo sobreescribimos
                if (!location.state.pendingSubject) {
                    setMessageSubject(`Envío de documento: ${location.state.fileName}`);
                    setMessageBody(`Adjunto el documento solicitado: ${location.state.fileName}`);
                }
                
                try {
                    // Si es una URL de la nube, la procesamos para mostrar el visor en el Dash
                    if (location.state.isFromCloud) {
                        setAttachments([{
                            name: location.state.fileName || "Archivo_Nube",
                            url: location.state.pendingAttachment,
                            isCloud: true
                        }]);
                    } else {
                        // Si es un blob local (como lo tenías antes)
                        const response = await fetch(location.state.pendingAttachment);
                        const blob = await response.blob();
                        const file = new File([blob], location.state.fileName || 'documento.pdf', { type: 'application/pdf' });
                        file.url = location.state.pendingAttachment; 
                        setAttachments([file]); 
                    }
                } catch (error) {
                    console.error("Error cargando adjunto", error);
                }
            }
        };
        
        if (!loading) loadFromState(); // Esperar a que los contactos carguen
    }, [location.state, loading, unifiedContacts]);

    const getPriorityColor = (p) => {
        switch(p) {
            case 'alta': return '#d32f2f'; 
            case 'media': return '#ed6c02'; 
            case 'baja': return '#2e7d32'; 
            default: return '#757575';
        }
    };

    const getPriorityLabel = (p) => {
        switch(p) {
            case 'alta': return 'URGENTE';
            case 'media': return 'NORMAL';
            case 'baja': return 'BAJA';
            default: return '';
        }
    };

    const handleAddTask = () => {
        if(!newTaskText.trim()) return;
        const newTask = {
            id: Date.now(),
            text: newTaskText,
            priority: newTaskPriority,
            completed: false,
            date: currentDate.format('YYYY-MM-DD')
        };
        setAdminTasks([...adminTasks, newTask]);
        setNewTaskText('');
    };

    const toggleTask = (id) => {
        setAdminTasks(adminTasks.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
    };

    const deleteTask = (id) => {
        setConfirmDialog({
            open: true,
            content: "¿Borrar esta tarea?",
            onConfirm: () => {
                setAdminTasks(adminTasks.filter(t => t.id !== id));
                setConfirmDialog(prev => ({...prev, open: false}));
            }
        });
    };

    const processMacro = (content, data) => {
        if (!content) return '';
        return content.replace(/{(\w+)}/g, (_, key) => data[key] || `{${key}}`);
    };

    const getContactValue = (contact) => {
        if (typeof contact === 'string') return contact; 
        return contact.email || contact.value || contact.phone || '';
    };

    const handleSendMessage = async () => {
        if (selectedRecipients.length === 0 || !messageBody) {
             setNotification({ open: true, message: "Indique destinatario y mensaje.", severity: 'warning' });
             return;
        }
        
        setIsSending(true);
        try {
            const token = localStorage.getItem('token'); 
            
            if (msgTab === 0) { 
                for (const recipient of selectedRecipients) {
                     await fetch(`${API_URL}/api/messages/internal`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ 
                            senderId: user.id, 
                            receiverId: recipient.id, 
                            subject: messageSubject || 'Interno', 
                            content: messageBody 
                        })
                    });
                }
            } else { 
                const emails = selectedRecipients.map(c => getContactValue(c)).join(',');
                const formData = new FormData();
				formData.append('to', emails);
				formData.append('subject', messageSubject);
				formData.append('content', messageBody);
				formData.append('senderId', user.id);

				// Si viene de Gestión Documental
				if (location.state?.pendingAttachment && location.state?.isFromCloud) {
					formData.append('existingFileUrl', location.state.pendingAttachment);
				} else {
					attachments.forEach(file => formData.append('attachments', file));
				}
                
                const res = await fetch(`${API_URL}/api/messages/external`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
                if (!res.ok) throw new Error("Error del servidor");
            }
            setNotification({ open: true, message: "Mensaje enviado exitosamente", severity: 'success' });
            setMessageBody(''); 
            setSelectedRecipients([]); 
            setAttachments([]);
        } catch (e) { 
            setNotification({ open: true, message: "Falla en envío del mensaje", severity: 'error' });
        } 
        finally { setIsSending(false); }
    };

    const handleDeleteContact = (id) => {
         setConfirmDialog({
            open: true,
            content: "¿Borrar contacto permanentemente?",
            onConfirm: async () => {
                const token = localStorage.getItem('token');
                await fetch(`${API_URL}/api/directory/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                setNotification({ open: true, message: "Contacto borrado", severity: 'success' });
                setSelectedDirContact(null);
                fetchUnifiedData();
                setConfirmDialog(prev => ({...prev, open: false}));
            }
        });
    };

    if (loading) return <Box sx={{p:5, textAlign:'center'}}><CircularProgress /></Box>;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Box sx={{ p: 3, flexGrow: 1 }}>
                
                <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: '#f0f4f8', borderRadius: 3, borderLeft: '8px solid #1976d2' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Typography variant="h4" fontWeight="bold" color="primary">Hola, {user?.name}</Typography>
                            <Typography variant="body1" color="text.secondary">{isAsistente ? 'Logística y Recepción' : 'Panel de Control Médico'}</Typography>
                        </Grid>
                        {isClinical && (
                            <Grid item xs={12} md={5}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
                                    <Button variant="contained" color="primary" onClick={() => navigate('/recipes')}>💊 RECETA</Button>
                                    <Button variant="contained" color="secondary" onClick={() => navigate('/patients')}>👤 PACIENTE</Button>
                                    <Button variant="contained" color="success" onClick={() => navigate('/informes')}>📄 INFORME</Button>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Paper elevation={3} sx={{ p: 3, borderTop: '4px solid #1976d2', height: '100%' }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                📩 Hub de Comunicaciones
                            </Typography>
                            
                            <Tabs value={msgTab} onChange={(_, v) => { setMsgTab(v); setSelectedRecipients([]); }} sx={{ mb: 2 }}>
                                <Tab label="Mensajería Interna" />
                                <Tab label="Email Profesional" />
                            </Tabs>

                            <Autocomplete
                                multiple freeSolo={msgTab === 1} fullWidth disablePortal
                                isOptionEqualToValue={(option, value) => option.id === (value?.id || value)}
                                options={unifiedContacts.filter(c => msgTab === 0 ? c.type === 'internal' : true)}
                                getOptionLabel={(o) => {
                                    if (typeof o === 'string') return o;
                                    return `${o.label} (${o.condition || o.category || 'Contacto'} - ${o.cedula || 'Externo'})`;
                                }}
                                value={selectedRecipients}
                                onChange={(_, v) => setSelectedRecipients(v)}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const label = typeof option === 'string' ? option : option.label;
                                        const email = typeof option === 'string' ? option : getContactValue(option);
                                        return <Chip variant="outlined" label={msgTab === 1 ? `${label} <${email}>` : label} size="small" {...getTagProps({ index })} />;
                                    })
                                }
                                renderInput={(p) => <TextField {...p} label={msgTab === 0 ? "Buscar Médico..." : "Destinatarios (Email)..."} size="small" sx={{ mb: 2 }} />}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <li key={key} {...optionProps} style={{ borderBottom: '1px solid #eee' }}>
                                            <Box sx={{ width: '100%' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2"><strong>{option.label}</strong></Typography>
                                                    <Chip label={option.condition || 'Contacto'} size="small" color={option.type === 'internal' ? "primary" : "default"} sx={{ fontSize: '9px', height: 18 }} />
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">{getContactValue(option)}</Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                            />

                            <TextField fullWidth label="Asunto" size="small" sx={{ mb: 2 }} value={messageSubject} onChange={(e) => setMessageSubject(e.target.value)} />
                            
                            <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold', color: 'text.secondary' }}>Plantillas:</Typography>
                                {macros.map((m) => (
                                    <Chip key={m.id} label={m.name} size="small" color="primary" variant="outlined" onClick={() => {
                                        const firstRecipientName = selectedRecipients.length > 0 ? (typeof selectedRecipients[0] === 'string' ? selectedRecipients[0] : selectedRecipients[0].label) : 'Paciente';
                                        const text = processMacro(m.content, { patientName: firstRecipientName, doctorName: user?.name, date: dayjs().format('DD/MM/YYYY') });
                                        setMessageBody(prev => (prev ? prev + '\n' : '') + text);
                                    }} clickable />
                                ))}
                                <IconButton size="small" onClick={() => navigate('/config')} title="Configurar Macros"><SettingsIcon fontSize="small" /></IconButton>
                            </Box>

                            <TextField fullWidth multiline rows={5} placeholder="Redacte su comunicación aquí..." value={messageBody} onChange={(e) => setMessageBody(e.target.value)} sx={{ mb: 2 }} />

                            {attachments.length > 0 && attachments[0].url && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #dee2e6' }}>
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>👁️ Verificando: <strong>{attachments[0].name}</strong></Typography>
                                    <Box sx={{ height: '250px', width: '100%', borderRadius: 1, border: '1px solid #ccc', overflow: 'hidden' }}>
                                        {attachments[0].url.toLowerCase().includes('.pdf') ? <iframe src={`${attachments[0].url}#toolbar=0`} width="100%" height="100%" style={{ border: 'none' }} /> : <img src={attachments[0].url} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
                                    </Box>
                                    <Button size="small" color="error" onClick={() => setAttachments([])} sx={{ mt: 1 }}>Eliminar adjunto</Button>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <input type="file" multiple id="attach-file" style={{ display: 'none' }} onChange={(e) => setAttachments(Array.from(e.target.files || []))} />
                                    <label htmlFor="attach-file"><Button variant="outlined" component="span" startIcon={<AttachFileIcon />} size="small" disabled={msgTab === 0}>Adjuntar</Button></label>
                                    <IconButton size="small" color="primary" onClick={() => setHelpOpen(true)}><Tooltip title="Ayuda"><WarningIcon fontSize="small" /></Tooltip></IconButton>
                                    {attachments.length > 0 && <Typography variant="caption" color="success.main">✓ {attachments.length} archivo(s)</Typography>}
                                </Box>
                                <Button variant="contained" color={msgTab === 0 ? "primary" : "success"} onClick={handleSendMessage} disabled={isSending} startIcon={<SendIcon />}>{msgTab === 0 ? "Enviar Interno" : "Enviar Email"}</Button>
                            </Box>
                        </Paper>
                        <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="xs">
                            <DialogTitle>Gestión de Adjuntos</DialogTitle>
                            <DialogContent dividers><Typography variant="body2">Para adjuntar historias, use "Imprimir a PDF" en la vista de reportes.</Typography></DialogContent>
                            <DialogActions><Button onClick={() => setHelpOpen(false)}>Entendido</Button></DialogActions>
                        </Dialog>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Paper elevation={3} sx={{ p: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ p: 2, bgcolor: '#455a64', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AssignmentIcon /> Tareas & Recordatorios</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
                                    <IconButton onClick={() => setCurrentDate(currentDate.subtract(1, 'day'))} size="small" sx={{ color: 'white' }}><ChevronLeftIcon /></IconButton>
                                    <DatePicker value={currentDate} onChange={(v) => setCurrentDate(v || dayjs())} format="DD/MM/YYYY" sx={{ input: { color: 'white', py: 1, width: '90px', textAlign: 'center', fontWeight: 'bold' }, fieldset: { border: 'none' }, svg: { color: 'white' } }} />
                                    <IconButton onClick={() => setCurrentDate(currentDate.add(1, 'day'))} size="small" sx={{ color: 'white' }}><ChevronRightIcon /></IconButton>
                                </Box>
                            </Box>
                            <Box sx={{ p: 2, flexGrow: 1, bgcolor: '#fafafa', overflowY: 'auto', maxHeight: '400px' }}>
                                <Paper variant="outlined" sx={{ p: 1, mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <TextField fullWidth placeholder="Nueva tarea..." size="small" variant="standard" value={newTaskText} onChange={(e) => setNewTaskText(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddTask()} />
                                    <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                                        <Select value={newTaskPriority} onChange={(e) => setNewTaskPriority(e.target.value)}>
                                            <MenuItem value="alta"><Typography color="error" fontWeight="bold">Alta</Typography></MenuItem>
                                            <MenuItem value="media"><Typography color="warning.main" fontWeight="bold">Media</Typography></MenuItem>
                                            <MenuItem value="baja"><Typography color="success.main" fontWeight="bold">Baja</Typography></MenuItem>
                                        </Select>
                                    </FormControl>
                                    <IconButton color="primary" onClick={handleAddTask}><AddTaskIcon /></IconButton>
                                </Paper>
                                <List dense>
                                    {adminTasks.filter(t => t.date === currentDate.format('YYYY-MM-DD')).length === 0 ? <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>No hay tareas.</Typography> : adminTasks.filter(t => t.date === currentDate.format('YYYY-MM-DD')).map(task => (
                                        <Paper key={task.id} elevation={1} sx={{ mb: 1, borderLeft: `6px solid ${getPriorityColor(task.priority)}`, bgcolor: task.completed ? '#f5f5f5' : 'white', transition: '0.3s', '&:hover': { boxShadow: 3 } }}>
                                            <ListItem secondaryAction={<IconButton edge="end" size="small" onClick={() => deleteTask(task.id)}><DeleteIcon fontSize="small" color="disabled" /></IconButton>}>
                                                <Checkbox edge="start" checked={task.completed} onChange={() => toggleTask(task.id)} color="primary" />
                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="body2" sx={{ textDecoration: task.completed ? 'line-through' : 'none', color: task.completed ? 'text.disabled' : 'text.primary', fontWeight: 'medium' }}>{task.text}</Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}><Chip label={getPriorityLabel(task.priority)} size="small" sx={{ height: 16, fontSize: '0.65rem', bgcolor: getPriorityColor(task.priority), color: 'white', fontWeight: 'bold' }} /></Box>
                                                </Box>
                                            </ListItem>
                                        </Paper>
                                    ))}
                                </List>
                            </Box>
                        </Paper>
                    </Grid>

                    {(isAdmin || isAsistente) && (
                        <Grid item xs={12}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={5}>
                                    <Paper elevation={3} sx={{ p: 3, borderTop: '4px solid #ed6c02', height: '100%' }}>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><ContactPhoneIcon color="warning" /> Gestión de Directorio</Typography>
                                        <Autocomplete fullWidth options={unifiedContacts.filter((c) => ['whatsapp', 'email'].includes(c.type))} getOptionLabel={(o) => o.label || ''} value={selectedDirContact} onChange={(_, v) => { setSelectedDirContact(v); if(v) setDirForm({ name: v.label.split(' (')[0], value: v.value, category: v.category || 'General' }); }} renderInput={(p) => <TextField {...p} label="Buscar contacto..." size="small" />} sx={{ mb: 2 }} />
                                        
                                        <Tabs value={dirTab} onChange={(_, v) => setDirTab(v)} sx={{ mb: 2, minHeight: 30 }} ><Tab label="WhatsApp" sx={{ py: 0, minHeight: 30 }} /><Tab label="Correo" sx={{ py: 0, minHeight: 30 }} /></Tabs>
                                        <TextField fullWidth label="Nombre / Empresa" size="small" sx={{ mb: 1.5 }} value={dirForm.name} onChange={e => setDirForm({...dirForm, name: e.target.value})} />
                                        <TextField fullWidth label="Dato" size="small" sx={{ mb: 1.5 }} value={dirForm.value} onChange={e => setDirForm({...dirForm, value: e.target.value})} />
                                        <Select fullWidth size="small" value={dirForm.category} onChange={(e) => setDirForm({...dirForm, category: e.target.value})} sx={{ mb: 2 }}><MenuItem value="Proveedor">Proveedor</MenuItem><MenuItem value="Contratista">Contratista</MenuItem><MenuItem value="Colaborador">Colaborador</MenuItem><MenuItem value="General">General</MenuItem></Select>

                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button fullWidth variant="contained" color="warning" onClick={async () => {
                                                const token = localStorage.getItem('token'); 
                                                const method = selectedDirContact ? 'PUT' : 'POST';
                                                const url = selectedDirContact ? `${API_URL}/api/directory/${selectedDirContact.id}` : `${API_URL}/api/directory`;
                                                const res = await fetch(url, { method, headers: { 'Content-Type':'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ institutionId: user.institutionId, name: dirForm.name, value: dirForm.value, type: dirTab === 0 ? 'whatsapp' : 'email' }) });
                                                if(res.ok) { setNotification({ open: true, message: "Contacto guardado", severity: 'success' }); setSelectedDirContact(null); setDirForm({name:'', value:'', category:'General'}); fetchUnifiedData(); }
                                            }}>{selectedDirContact ? "Actualizar" : "Guardar"}</Button>
                                            {selectedDirContact && <IconButton color="error" onClick={() => handleDeleteContact(selectedDirContact.id)}><DeleteIcon /></IconButton>}
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <Paper elevation={3} sx={{ borderTop: '4px solid #4caf50', p: 3, height: '100%' }}>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2e7d32' }}>Listado Profesional</Typography>
                                        <Tabs value={activeLogTab} onChange={(_, v) => { setActiveLogTab(v); setSelectedLogContacts([]); }} sx={{ mb: 2 }}><Tab label="Proveedores" /><Tab label="Contratistas" /><Tab label="Colaboradores" /></Tabs>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <Autocomplete multiple fullWidth options={unifiedContacts.filter((c) => ['Proveedor', 'Contratista', 'Colaborador'].includes(c.category)).filter((c) => c.category === ['Proveedor', 'Contratista', 'Colaborador'][activeLogTab])} getOptionLabel={(o) => o.label || ''} value={selectedLogContacts} onChange={(_, v) => setSelectedLogContacts(v)} renderInput={(p) => <TextField {...p} label={`Seleccionar...`} />} renderTags={(v, g) => v.map((o, i) => (<Chip label={o.label} {...g({ index: i })} size="small" />))} />
                                            <Button variant="contained" color="success" sx={{ height: 56, minWidth: 120 }} startIcon={<WhatsAppIcon />} disabled={selectedLogContacts.length === 0} onClick={() => { selectedLogContacts.forEach(c => window.open(`https://wa.me/${c.value ? c.value.replace(/\D/g, '') : ''}?text=Hola...`, '_blank')); }}>Chat</Button>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>

                {/* COMPONENTES VISUALES: Snackbar y Diálogo */}
                <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert severity={notification.severity} variant="filled">{notification.message}</Alert>
                </Snackbar>

                <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: '#1976d2' }}><WarningIcon color="primary" /> Luiatro Health dice:</DialogTitle>
                    <DialogContent dividers><Typography variant="body1">{confirmDialog.content}</Typography></DialogContent>
                    <DialogActions><Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} color="inherit">Cancelar</Button><Button onClick={confirmDialog.onConfirm} variant="contained" color="error">Confirmar</Button></DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default DashboardPage;